import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Camera, Images, X, Sparkles, Send, RotateCcw, Save, CheckCircle2, AlertTriangle } from "lucide-react";
import { formatDateTime } from "@/lib/date";
import { toast } from "sonner";
import { tx } from "@/i18n";
import { SuppliesBlock } from "@/components/SupplyBlocks";
import { CameraDialog } from "@/components/CameraDialog";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;

function Chip({ active, label, onClick, disabled, testId }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-sm border transition-colors duration-150 active:scale-95 disabled:opacity-60 disabled:pointer-events-none ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50 hover:bg-primary/5"}`}>
      {label}
    </button>
  );
}

function PhotoStrip({ photos, onDelete, editable, onAdd, uploading, cat, missing }) {
  const ref = useRef(null);
  const camRef = useRef(null);
  const [live, setLive] = useState(false);
  const pick = (e) => { onAdd(cat, Array.from(e.target.files || [])); e.target.value = ""; };
  return (
    <div className="flex items-center gap-2 flex-wrap mt-2">
      {photos.map((p) => (
        <div key={p.id} className="relative" data-testid={`report-photo-${p.id}`}>
          <a href={`${ORIGIN}${p.url}`} target="_blank" rel="noreferrer"><img src={`${ORIGIN}${p.url}?w=400`} loading="lazy" decoding="async" alt={p.filename} className="h-16 w-16 rounded-lg object-cover border border-border" /></a>
          {editable && <button type="button" onClick={() => onDelete(p.id)} className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full p-0.5" data-testid={`report-photo-delete-${p.id}`}><X className="h-3 w-3" /></button>}
        </div>
      ))}
      {editable && (
        <>
          <CameraDialog open={live} onClose={() => setLive(false)} onDone={(files) => onAdd(cat, files)} onFallback={() => camRef.current?.click()} />
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`report-photo-camera-input-${cat}`} />
          <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid={`report-photo-input-${cat}`} />
          <Button type="button" size="sm" variant="outline" onClick={() => setLive(true)} disabled={uploading} className={`gap-1.5 ${missing ? "border-red-400 text-red-600" : ""}`} data-testid={`report-photo-add-${cat}`}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} {tx("Foto maken")}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => ref.current?.click()} disabled={uploading} className="gap-1.5 text-muted-foreground" title={tx("Uit galerij kiezen")} data-testid={`report-photo-gallery-${cat}`}>
            <Images className="h-4 w-4" /> <span className="sr-only sm:not-sr-only">{tx("Galerij")}</span>
          </Button>
        </>
      )}
    </div>
  );
}

function CategoryBlock({ c, idx, form, setForm, editable, photos, onAdd, onDelete, uploading }) {
  const toggle = (label) => setForm((f) => ({ ...f, keywords: f.keywords.includes(label) ? f.keywords.filter((k) => k !== label) : [...f.keywords, label] }));
  const missing = c.required_photo && photos.length === 0;
  return (
    <div className="py-3 border-b border-border last:border-0" data-testid={`report-cat-${c.key}`}>
      <p className="text-sm font-semibold mb-2 flex items-center gap-2">
        <span className="text-muted-foreground">{idx}.</span> {tx(c.label)}
        {c.required_photo && <Badge variant="outline" className={missing ? "border-red-400 text-red-600" : "border-emerald-500 text-emerald-600"}>{tx("Foto verplicht")}</Badge>}
      </p>
      {c.keywords.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {c.keywords.map((k) => <Chip key={k.id} label={tx(k.label)} active={form.keywords.includes(k.label)} disabled={!editable} onClick={() => toggle(k.label)} testId={`report-kw-${c.key}-${k.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />)}
        </div>
      )}
      {editable ? (
        <Textarea value={form.notes[c.key] || ""} onChange={(e) => setForm((f) => ({ ...f, notes: { ...f.notes, [c.key]: e.target.value } }))} rows={1} placeholder={tx("Toelichting (optioneel)")} className="mt-2 resize-none text-sm min-h-[36px]" data-testid={`report-note-${c.key}`} />
      ) : (form.notes[c.key] && <p className="text-sm text-muted-foreground mt-2 italic">{form.notes[c.key]}</p>)}
      {c.photo && (editable || photos.length > 0) && <PhotoStrip photos={photos} editable={editable} onAdd={onAdd} onDelete={onDelete} uploading={uploading === c.key} cat={c.key} missing={missing} />}
    </div>
  );
}

function StoryBox({ report, isAdmin, onUpdate }) {
  const [story, setStory] = useState(report.story || "");
  const [busy, setBusy] = useState("");
  useEffect(() => setStory(report.story || ""), [report.story]);
  const call = async (key, fn) => {
    setBusy(key);
    try { const { data } = await fn(); onUpdate(data); return data; } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const send = async () => {
    if (story !== report.story) await call("save", () => api.put(`/reports/${report.id}/story`, { story }));
    const d = await call("send", () => api.post(`/reports/${report.id}/send`));
    if (d) toast.success(tx("Verslag verstuurd naar de woningchat"));
  };
  const sent = report.status === "sent";
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3.5 mt-3" data-testid="report-story-box">
      <div className="text-sm font-semibold flex items-center gap-1.5 mb-2"><Sparkles className="h-4 w-4 text-primary" /> {tx("Verhaaltje")}
        {sent ? <Badge className="bg-emerald-600 text-white ml-auto" data-testid="report-status-sent">{tx("Verstuurd")}</Badge>
          : <Badge className="bg-amber-500 text-white ml-auto" data-testid="report-status-submitted">{tx("Wacht op controle")}</Badge>}
      </div>
      {isAdmin && !sent ? (
        <Textarea value={story} onChange={(e) => setStory(e.target.value)} rows={6} className="text-sm bg-card" data-testid="report-story-input" />
      ) : <p className="text-sm whitespace-pre-line leading-relaxed" data-testid="report-story-text">{report.story}</p>}
      {report.submitted_at && <p className="text-[11px] text-muted-foreground mt-2" data-testid="report-submitted-at">{tx("Ingediend door")} {report.submitted_by} · {formatDateTime(report.submitted_at)}</p>}
      {sent && <p className="text-[11px] text-muted-foreground mt-0.5">{tx("Verstuurd door")} {report.sent_by} · {formatDateTime(report.sent_at)}</p>}
      {isAdmin && !sent && (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <Button size="sm" onClick={send} disabled={!!busy || !story.trim()} className="gap-1.5 bg-primary hover:bg-primary/90" data-testid="report-approve-send-btn">
            {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {tx("Goedkeuren & versturen")}
          </Button>
          <Button size="sm" variant="outline" onClick={() => call("regen", () => api.post(`/reports/${report.id}/regenerate`))} disabled={!!busy} className="gap-1.5" data-testid="report-regenerate-btn">
            {busy === "regen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {tx("Opnieuw genereren")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => call("reopen", () => api.post(`/reports/${report.id}/reopen`))} disabled={!!busy} className="gap-1.5" data-testid="report-reopen-btn">
            <RotateCcw className="h-4 w-4" /> {tx("Steekwoorden aanpassen")}
          </Button>
        </div>
      )}
    </div>
  );
}

export function ReportPanel({ booking, onChange }) {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [cfg, setCfg] = useState(null);
  const [report, setReport] = useState(null);
  const [form, setForm] = useState({ keywords: [], notes: {}, free_text: "", supplies: [] });
  const [busy, setBusy] = useState("");
  const [uploading, setUploading] = useState("");

  const apply = (r) => { setReport(r); if (r) setForm({ keywords: r.keywords || [], notes: r.notes || {}, free_text: r.free_text || "", supplies: r.supplies || [] }); onChange?.(); };
  useEffect(() => {
    api.get(`/reports/booking/${booking.id}`).then(({ data }) => { setCfg(data); apply(data.report); }).catch((e) => { toast.error(apiError(e)); setCfg({ categories: [] }); });
    // eslint-disable-next-line
  }, [booking.id]);

  const save = () => api.put(`/reports/booking/${booking.id}`, form).then(({ data }) => { apply(data); return data; });
  const addPhotos = async (cat, files) => {
    setUploading(cat);
    try {
      await save();
      let last;
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f); fd.append("category", cat);
        last = (await api.post(`/reports/booking/${booking.id}/photo`, fd)).data;
      }
      if (last) apply(last);
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(""); }
  };
  const delPhoto = (pid) => api.delete(`/reports/${report.id}/photo/${pid}`).then(({ data }) => apply(data)).catch((e) => toast.error(apiError(e)));
  const run = async (key) => {
    setBusy(key);
    try {
      const r = await save();
      if (key === "submit") { const { data } = await api.post(`/reports/${r.id}/submit`); apply(data); toast.success(tx("Verhaaltje gemaakt")); }
      else toast.success(tx("Opgeslagen"));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (!cfg) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  const status = report?.status || "draft";
  const editable = status === "draft";
  const photos = (k) => (report?.photos || []).filter((p) => p.category === k);
  const meterMissing = cfg.categories.some((c) => c.required_photo && photos(c.key).length === 0);

  return (
    <div className="max-h-[65vh] overflow-y-auto pr-1" data-testid="report-panel">
      {cfg.categories.map((c, i) => (
        <CategoryBlock key={c.key} c={c} idx={i + 1} form={form} setForm={setForm} editable={editable} photos={photos(c.key)} onAdd={addPhotos} onDelete={delPhoto} uploading={uploading} />
      ))}
      <SuppliesBlock supplies={form.supplies} setSupplies={(v) => setForm((f) => ({ ...f, supplies: v }))} editable={editable} sent={status === "sent"} />
      <div className="py-3">
        <p className="text-sm font-semibold mb-2">{tx("Bijzonderheden")}</p>
        {editable ? <Textarea value={form.free_text} onChange={(e) => setForm((f) => ({ ...f, free_text: e.target.value }))} rows={3} placeholder={tx("Vrije tekst...")} className="text-sm" data-testid="report-free-text" />
          : <p className="text-sm text-muted-foreground whitespace-pre-line">{form.free_text || "—"}</p>}
      </div>
      {editable ? (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          {meterMissing && <p className="basis-full text-xs text-red-600 flex items-center gap-1" data-testid="report-meter-missing"><AlertTriangle className="h-3.5 w-3.5" /> {tx("Meterstand foto is verplicht voor deze woning")}</p>}
          <Button onClick={() => run("submit")} disabled={!!busy || meterMissing} className="gap-1.5 bg-primary hover:bg-primary/90" data-testid="report-submit-btn">
            {busy === "submit" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy === "submit" ? tx("Verhaaltje maken...") : tx("Indienen & verhaaltje maken")}
          </Button>
          <Button variant="outline" onClick={() => run("save")} disabled={!!busy} className="gap-1.5" data-testid="report-save-btn">
            {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {tx("Concept opslaan")}
          </Button>
        </div>
      ) : (
        <>
          {!isAdmin && status === "submitted" && <p className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> {tx("Ingediend — de beheerder controleert en verstuurt het verslag.")}</p>}
          <StoryBox report={report} isAdmin={isAdmin} onUpdate={apply} />
        </>
      )}
    </div>
  );
}
